'use client';

import { useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Calculator, Moon, Orbit, Sparkles } from 'lucide-react';
import { trackGrowthEvent } from '@/lib/analytics';
import ExperienceAtmosphere from '@/components/ui/ExperienceAtmosphere';

interface HeroSectionProps {
    isAuthenticated?: boolean;
}

const tools = [
    {
        id: 'life-path',
        label: 'Life Path',
        description: 'Calculate your core numerology pattern from your birth date.',
        preview: 'Life Path 8 combines ambition, stewardship, and learning to use influence with integrity.',
        href: '/life-path-number-calculator',
        icon: Calculator,
    },
    {
        id: 'moon-sign',
        label: 'Moon Sign',
        description: 'Explore the emotional rhythm behind your public personality.',
        preview: 'Your Moon sign describes what restores you, what overwhelms you, and how you process emotion.',
        href: '/moon-sign-calculator',
        icon: Moon,
    },
    {
        id: 'birth-chart',
        label: 'Birth Chart',
        description: 'Begin with your Sun, Moon, Rising sign, houses, and timing.',
        preview: 'A complete chart connects identity, emotional needs, outer style, and the life areas asking for attention.',
        href: '/free-birth-chart-reading',
        icon: Orbit,
    },
] as const;

const HeroSection = ({ isAuthenticated = false }: HeroSectionProps) => {
    const [selectedId, setSelectedId] = useState<(typeof tools)[number]['id']>('life-path');
    const selected = tools.find((tool) => tool.id === selectedId) || tools[0];

    return (
        <section id="hero" className="experience-page relative flex min-h-[88svh] items-center overflow-hidden pt-24">
            <ExperienceAtmosphere priority />

            <div className="container relative z-10 mx-auto px-5 py-14 md:py-20">
                <div className="mx-auto w-full max-w-5xl overflow-hidden text-center">
                    <div className="mx-auto inline-flex items-center gap-2 text-sm font-semibold text-accent">
                        <Sparkles size={16} aria-hidden="true" />
                        Two-minute daily brief
                    </div>

                    <h1 className="mx-auto mt-5 max-w-[19rem] break-words font-playfair text-4xl font-bold leading-[1.05] text-foreground [text-wrap:balance] sm:max-w-4xl sm:text-5xl md:text-7xl">
                        A clearer day starts with one brief.
                    </h1>
                    <p className="mx-auto mt-6 max-w-[20rem] text-lg leading-8 text-[var(--experience-text-soft)] md:max-w-2xl md:text-xl">
                        YourAura combines chart timing, numerology cycles, and optional palm context into one focus, one caution, and one reflection.
                    </p>

                    <div className="mx-auto mt-8 flex w-full max-w-[335px] flex-col items-stretch justify-center gap-3 sm:max-w-none sm:flex-row sm:items-center">
                        <Link
                            href={isAuthenticated ? '/dashboard' : '/signup'}
                            className="manuscript-button inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent sm:w-auto"
                        >
                            Get My Free Brief
                            <ArrowRight size={17} aria-hidden="true" />
                        </Link>
                        <Link
                            href="#free-tools"
                            className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-[var(--experience-border)] px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:border-accent/50 hover:bg-[var(--experience-surface-quiet)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent sm:w-auto"
                        >
                            Explore Free Tools
                        </Link>
                    </div>

                    <p className="mt-9 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--experience-text-faint)]">
                        Start with a free calculation
                    </p>
                    <div className="mx-auto mt-9 grid w-full max-w-[335px] grid-cols-1 gap-2 sm:max-w-3xl sm:grid-cols-3 sm:gap-3" aria-label="Choose a free reading">
                        {tools.map((tool) => {
                            const ToolIcon = tool.icon;
                            const active = selected.id === tool.id;
                            return (
                                <button
                                    key={tool.id}
                                    type="button"
                                    aria-pressed={active}
                                    onClick={() => {
                                        setSelectedId(tool.id);
                                        trackGrowthEvent('tool_selected', { tool: tool.id, placement: 'homepage_hero' });
                                    }}
                                    className={`card-quiet card-interactive min-h-20 min-w-0 w-full p-3 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 sm:min-h-24 sm:p-4 sm:text-left ${active
                                        ? 'card-selected text-foreground'
                                        : 'text-muted-foreground'
                                        }`}
                                >
                                    <span className="flex flex-col items-center gap-2 text-sm font-semibold sm:flex-row sm:text-base">
                                        <ToolIcon size={18} className={active ? 'text-accent' : 'text-[#ead8b7]/60'} aria-hidden="true" />
                                        {tool.label}
                                    </span>
                                    <span className="mt-2 hidden text-xs leading-5 text-current opacity-75 sm:block">{tool.description}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="card-feature mx-auto mt-8 w-full max-w-[335px] p-5 text-left sm:max-w-3xl sm:flex sm:items-center sm:justify-between sm:gap-8 sm:p-6">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">Example interpretation</p>
                            <p className="mt-2 leading-7 text-[var(--experience-text-soft)]">{selected.preview}</p>
                        </div>
                        <Link
                            href={selected.href}
                            onClick={() => trackGrowthEvent('tool_started', { tool: selected.id, placement: 'homepage_hero' })}
                            className="manuscript-button mt-5 inline-flex min-h-12 shrink-0 items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-bold sm:mt-0"
                        >
                            Get Free Result
                            <ArrowRight size={17} aria-hidden="true" />
                        </Link>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default HeroSection;
